import type { ProcessingStage, FeedMessage } from '../types/processing';

export const REFINE_STAGES: Omit<ProcessingStage, 'progress' | 'status'>[] = [
  { id: 'context', label: 'Context Integration', duration: 1500 },
  { id: 'compile', label: 'Compiling Dashboard', duration: 3000 },
  { id: 'reanalysis', label: 'Personalized Re-analysis', duration: 2000 },
  { id: 'reports', label: 'Report Generation', duration: 2500 },
];

export const REFINE_FEED_MESSAGES: FeedMessage[] = [
  { delay: 300, type: '', msg: 'Loading questionnaire responses...' },
  { delay: 800, type: 'highlight', msg: 'Business context received — personalizing analysis' },
  { delay: 1500, type: '', msg: 'Reviewing prior analysis findings...' },
  { delay: 2200, type: '', msg: 'Re-evaluating business health score with context...' },
  { delay: 2800, type: '', msg: 'Updating SWOT analysis for business model...' },
  { delay: 3400, type: 'finding', msg: 'Finding: Priorities aligned with retention metrics' },
  { delay: 4000, type: '', msg: 'Re-grading business areas by stage benchmarks...' },
  { delay: 4600, type: '', msg: 'Modeling growth scenarios for stated goals...' },
  { delay: 5200, type: 'highlight', msg: 'Tailored recommendations generated' },
  { delay: 5800, type: '', msg: 'Compiling personalized strategic report...' },
  { delay: 6400, type: '', msg: 'Generating PDF report...' },
  { delay: 7000, type: '', msg: 'Generating PPTX presentation...' },
  { delay: 7600, type: 'highlight', msg: '\u2713 Refinement complete — reports ready' },
];

export const PROCESSING_STAGES: Omit<ProcessingStage, 'progress' | 'status'>[] = [
  { id: 'ingest', label: 'Data Ingestion', duration: 1800 },
  { id: 'reconcile', label: 'Cross-Platform Reconciliation', duration: 2500 },
  { id: 'revenue', label: 'Revenue & Growth Analysis', duration: 2800 },
  { id: 'subscribers', label: 'Customer Health Assessment', duration: 2200 },
  { id: 'funnel', label: 'Funnel & Conversion Analysis', duration: 2500 },
  { id: 'economics', label: 'Unit Economics Calculation', duration: 2000 },
  { id: 'strategy', label: 'Strategic Insight Generation', duration: 3200 },
];

/**
 * The unified owner-audit pipeline runs as ONE continuous stream: the 7 analysis
 * stages first, then the 4 refine (report-compilation) stages on the SAME
 * Centrifugo channel. Refine wire events restart at `stage_number=1` with
 * `total_stages=4`, so they map to the combined indices
 * `ANALYSIS_STAGE_COUNT + (n - 1)`.
 */
export const ANALYSIS_STAGE_COUNT = PROCESSING_STAGES.length;
export const REFINE_TOTAL_STAGES = REFINE_STAGES.length;

const REFINE_LABELS = new Set<string>(REFINE_STAGES.map((s) => s.label));

/**
 * Is this stage event from the refine pass? `stage_started` carries
 * `total_stages=4`; both event types carry a `stage_name` unique to the refine
 * set — the name check covers `stage_completed`, which has no `total_stages`.
 */
export function isRefineStage(stageName?: string, totalStages?: number): boolean {
  return totalStages === REFINE_TOTAL_STAGES || (stageName != null && REFINE_LABELS.has(stageName));
}

export const FEED_MESSAGES: FeedMessage[] = [
  { delay: 300, type: '', msg: 'Connecting to data sources...' },
  { delay: 800, type: '', msg: 'Stripe API: 29,640 transaction records loaded' },
  { delay: 1400, type: '', msg: 'Google Analytics: 284K sessions imported (Jul 2023 \u2013 Mar 2025)' },
  { delay: 2000, type: '', msg: 'Meta Ads: 1,247 campaign records synced' },
  { delay: 2600, type: '', msg: 'Google Ads: 892 keyword-level records synced' },
  { delay: 3200, type: 'highlight', msg: 'Cross-platform reconciliation started...' },
  { delay: 3800, type: '', msg: 'Matching 31,779 transactions across platforms' },
  { delay: 4400, type: 'warn', msg: 'Warning: 3.2% transaction mismatch detected (Stripe vs GA)' },
  { delay: 5000, type: '', msg: 'Attribution window: 28-day click, 7-day view' },
  { delay: 5600, type: '', msg: 'Revenue trend analysis: 8 fiscal years detected' },
  { delay: 6200, type: 'finding', msg: 'Finding: Revenue CAGR 97% (2018-2024), decelerating to 9.5% current' },
  { delay: 6800, type: '', msg: 'Subscriber cohort analysis: 24 monthly cohorts' },
  { delay: 7400, type: 'alert', msg: 'Alert: -5.5% subscriber decline from peak (11,890 \u2192 11,240)' },
  { delay: 8000, type: '', msg: 'Funnel analysis: 3-stage conversion pipeline' },
  { delay: 8600, type: 'finding', msg: 'Finding: Visitor-to-paid rate 0.86% (industry avg 1.2-1.8%)' },
  { delay: 9200, type: '', msg: 'Calculating blended CPA across 6 channels...' },
  { delay: 9800, type: '', msg: 'LTV/CAC analysis: unit economics assessment' },
  { delay: 10400, type: 'highlight', msg: 'LTV:CAC ratio 7.4x \u2014 strong unit economics confirmed' },
  { delay: 11000, type: '', msg: 'Competitive benchmark: loading peer dataset...' },
  { delay: 11600, type: '', msg: 'SEO audit: crawling 12,400 indexed pages' },
  { delay: 12200, type: 'warn', msg: 'SEO Warning: Domain Authority 38 vs peer avg 52' },
  { delay: 12800, type: '', msg: 'AI Answer Engine Optimization: schema analysis' },
  { delay: 13400, type: 'finding', msg: 'AEO score: 34/100 \u2014 significant optimization opportunity' },
  { delay: 14000, type: '', msg: 'Market sizing: TAM/SAM/SOM triangulation' },
  { delay: 14600, type: '', msg: 'Generating strategic recommendations...' },
  { delay: 15200, type: 'highlight', msg: 'Identified 4 strategic pillars, 6 critical gaps' },
  { delay: 15800, type: '', msg: 'Building growth scenario models...' },
  { delay: 16400, type: 'highlight', msg: '\u2713 Analysis complete \u2014 47,382 data points processed' },
];
